import * as _ from "lodash";
import * as moment from "moment-timezone";
import { useCallback } from "react";
import * as React from "react";
import { useMappedState } from "redux-react-hook";

import {
  getDeliveryCutoff,
  getDeliveryRoute,
  getDeliveryWeekLeadTimes
} from "src/core/drops/utils/inherited-settings";
import {
  getDay,
  unavailableDeliveryDays
} from "src/core/orders/utils/order-deliveries";
import useMinimumLeadTime from "src/core/shared/hooks/useMinimumLeadTime";
import { IProductMaybeWithSubstitution } from "src/core/products/utils/filter-product-substitutions";
import {
  CustomerBranchFilterDefault,
  CustomerBranchFilterType,
  ProductStatus
} from "src/core/products/api/products.api";
import { IStore } from "src/core/shared/store";
import {
  IDrop,
  WeekDay
} from "src/core/drops/api/drops.api";
import { useDropShutdowns } from "src/modules/shutdowns/hooks/useDropShutdowns";

import useDaysToCutoff from "./useDaysToCutoff";
import { useLeadTimeOverrideCutoff } from "./useLeadTimeOverrideCutoff";
import { IOrderVariant } from "../../api/orders.api";
import useCutoffByDate from "./useCutoffByDate";

/**
 * Check if ordering is allowed on this day.
 *
 * @param {IOrder} order - order object.
 * @param {IDrop} drop - drop object.
 *
 * @return - Whether ordering is allowed on this day or not.
 */
const useOrderingNotAvailableForDay = (drop: IDrop | null) => {
  const daysToCutOff = useDaysToCutoff(drop);
  const { minimumLeadTime } = useMinimumLeadTime();
  const { checkShutdownDate } = useDropShutdowns(drop);
  const leadTimeOverrideForDate = useLeadTimeOverrideCutoff(drop);
  const getCutoffByDate = useCutoffByDate(drop);

  const {
    openDays, displaySettings, leadTimeGroups
  } = useMappedState(({ global }: IStore) => ({
    openDays: global.settings?.openDays,
    displaySettings: global.settings?.displaySettings,
    leadTimeGroups: global.settings?.leadTimeGroups || []
  }));

  const allowedBasedOnSubs = React.useCallback((product?: IProductMaybeWithSubstitution) => {
    if (!product?.substitution) {
      return null;
    }

    if (!drop?.groupedSettings.allow_substitutions?.value.allowed) {
      return `Product not available: ${product.substitution.message}`;
    } else if (!product.substitution?.replacementProduct.prices?.length) {
      return "Product not available";
    }

    return true;
  }, [ drop ]);

  const allowedBasedOnOpenDay = React.useCallback((deliveryDate: Date | null): boolean => {
    if (!deliveryDate) {
      return false;
    }

    const matchingOpenDay = _.find(openDays, od => {
      return moment.utc(deliveryDate, "YYYY-MM-DD").set("hours", 12).isBetween(moment.utc(od.startDate).startOf("day"), moment.utc(od.endDate).endOf("day"), "second");
    }
    );

    if (matchingOpenDay) {
      const matchingBranchFilter = _.find(matchingOpenDay.filters, openDayFilter => openDayFilter.branchId === drop?.id);
      // match the branch first and fall back to a customer level filter
      const matchingFilter = matchingBranchFilter || _.find(matchingOpenDay.filters, openDayFilter => openDayFilter.customerId === drop?.customerId);

      // logic to determine if it should or should not allow an open day to override
      // we should never return false based on an open day. If there is no match that allows it then we should fall back to
      // the standard delivery days settings used in super.canOrderOnDeliveryDate

      // matching filter is an inclusion so allow it
      if (matchingFilter?.filterType === CustomerBranchFilterType.Include) {
        return true;
      }

      if (matchingOpenDay.type === CustomerBranchFilterDefault.AllowAll) {
      // its allow all so if theres no matching filter
        if (!matchingFilter) {
        // default if no matches is to allow orders
          return true;
        }
      }
    }

    return false;
  }, [
    drop?.customerId,
    drop?.id,
    openDays
  ]);

  const orderingNotAvailableForDay = useCallback(
    (date: Date | null, orderVariant: IOrderVariant, product?: IProductMaybeWithSubstitution,): string | null => {
      if (!drop) {
        return "Drop info not available";
      }

      const deliveryRoute = getDeliveryRoute(drop.groupedSettings);
      const leadTimes = getDeliveryWeekLeadTimes(drop.groupedSettings);
      const defaultCutoff = getDeliveryCutoff(drop.groupedSettings);
      let productLeadTimeGroup = minimumLeadTime;

      if (product) {
        if (product.leadTimeGroup) {
          productLeadTimeGroup = product.leadTimeGroup;
        } else {
          const prodLtg = _.find(leadTimeGroups, ltg => ltg.id === product.leadTimeGroupId);

          if (prodLtg) {
            productLeadTimeGroup = prodLtg;
          }
        }
      }

      //order variant default lead time group takes priority
      if (orderVariant?.defaultProductLeadTimeGroup) {
        productLeadTimeGroup = orderVariant?.defaultProductLeadTimeGroup;
      }

      // made to order
      if (!date || !productLeadTimeGroup) {
        return null;
      }

      const leadTimeCutoff = leadTimeOverrideForDate(date);

      if (moment.utc().isAfter(leadTimeCutoff)) {
        return displaySettings?.order?.cutoffMessage ? displaySettings?.order?.cutoffMessage : "The cutoff for this delivery date has already passed";
      }

      const cutoffByDate = getCutoffByDate(date, "shortest", orderVariant.id, productLeadTimeGroup);

      if (cutoffByDate && moment.utc().isAfter(cutoffByDate)) {
        return displaySettings?.order?.cutoffMessage ? displaySettings?.order?.cutoffMessage : "The cutoff for this delivery date has already passed";
      }

      const daysToCut = daysToCutOff(date, orderVariant?.id, productLeadTimeGroup);
      const isoDeliveryDay = moment.utc(date).isoWeekday();

      if (orderVariant?.options?.allowedDeliveryDays) {
        if (!orderVariant?.options?.allowedDeliveryDays.includes(isoDeliveryDay as WeekDay)) {
          return `${orderVariant.name} orders cannot be created on ${moment
            .utc(date)
            .local()
            .format("dddd")}`;
        }
      }

      if (product) {
        if (product.prices && product.prices.length) {
          if (product.prices[ 0 ].effectiveDate && moment.utc(product.prices[ 0 ].effectiveDate).isAfter(moment.utc(date), "day")) {
            return `Product not available until ${moment.utc(product.prices[ 0 ].effectiveDate).format("DD/MM/YYYY")}`;
          }
        } else {
          return "Product not available for this date";
        }

        const allowedBySubs = allowedBasedOnSubs(product);

        if (typeof allowedBySubs === "string") {
          return allowedBySubs;
        }

        const currentProductStatus = product.currentStatus?.status;

        if (currentProductStatus === ProductStatus.Suspended || currentProductStatus === ProductStatus.Delisted) {
          return `Product is not available due to being ${currentProductStatus}`;
        }

        if (product.deliveryRoute && product.deliveryRoute[ 0 ]) {
          const deliveryDay = getDay(isoDeliveryDay);

          // check product delivery
          if (
            !deliveryDay ||
            (product.deliveryRoute[ 0 ].deliveryDays && product.deliveryRoute[ 0 ].deliveryDays[ deliveryDay ] === undefined)
          ) {
            return `The product ${product.partNumber} cannot be delivered on ${moment
              .utc(date)
              .format("dddd")}`;
          }
        }
      }

      if (!deliveryRoute || !leadTimes || !defaultCutoff || typeof daysToCut !== "number") {
        return "Failed to load cut-off data";
      } else if (daysToCut < 0) {
        return displaySettings?.order?.cutoffMessage || "This delivery date does not have enough advance notice.";
      }

      const hasShutdownDate = checkShutdownDate(date, orderVariant?.id || product?.orderVariantId);

      if (hasShutdownDate) {
        let message = "We are not accepting orders on this day due to being shutdown: ";

        if (hasShutdownDate?.shutdown.branchId) {
          message = "This date has been blocked from orders being placed due to a holiday: ";
        }

        return message += hasShutdownDate.shutdown.reason;
      }

      // if its an open day then allow
      if (allowedBasedOnOpenDay(date)) {
        return null;
      }

      const isUnavailableDeliveryDay = _.includes(
        unavailableDeliveryDays(deliveryRoute),
        moment
          .utc(date)
          .format("dddd")
      );

      return isUnavailableDeliveryDay ?
        `${moment
          .utc(date)
          .format("dddd")} is not available for deliveries.` :
        null;
    },
    [
      drop,
      minimumLeadTime,
      leadTimeOverrideForDate,
      getCutoffByDate,
      daysToCutOff,
      checkShutdownDate,
      allowedBasedOnOpenDay,
      leadTimeGroups,
      displaySettings?.order?.cutoffMessage,
      allowedBasedOnSubs
    ]
  );

  return orderingNotAvailableForDay;
};

export default useOrderingNotAvailableForDay;
